// ─── src/components/dashboard/MentorFilterBar.tsx ────────────────────────────
import { type FC } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";

export interface MentorFilters {
  search:    string;
  frequency: string;
  mode:      string;
  city:      string;
  topic:     string;
}

export const DEFAULT_MENTOR_FILTERS: MentorFilters = {
  search:    "",
  frequency: "all",
  mode:      "all",
  city:      "all",
  topic:     "all",
};

interface Props {
  filters:  MentorFilters;
  cities:   string[];   // pre-computed unique city list from stats endpoint
  topics:   string[];   // pre-computed unique topic list from stats endpoint
  onChange: (f: MentorFilters) => void;
  onReset:  () => void;
}

const isDirty = (f: MentorFilters) =>
  f.search !== "" ||
  f.frequency !== "all" ||
  f.mode !== "all" ||
  f.city !== "all" ||
  f.topic !== "all";

const Select: FC<{
  value:    string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}> = ({ value, onChange, children }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-brand-card border border-brand-border text-white text-sm
               rounded-xl px-3 py-2 focus:outline-none focus:ring-2
               focus:ring-brand-orange/50 focus:border-brand-orange/50
               transition-colors cursor-pointer min-w-[130px]"
  >
    {children}
  </select>
);

export const MentorFilterBar: FC<Props> = ({ filters, cities, topics, onChange, onReset }) => {

  const set = (key: keyof MentorFilters) => (value: string) =>
    onChange({ ...filters, [key]: value });

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search name, email, company…"
            value={filters.search}
            onChange={(e) => set("search")(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-brand-card border border-brand-border
                       text-white text-sm rounded-xl placeholder:text-brand-muted
                       focus:outline-none focus:ring-2 focus:ring-brand-orange/50
                       focus:border-brand-orange/50 transition-colors"
          />
        </div>

        <SlidersHorizontal size={15} className="text-brand-muted shrink-0" />

        {/* Frequency */}
        <Select value={filters.frequency} onChange={set("frequency")}>
          <option value="all">All Frequencies</option>
          <option value="on-demand">On-demand</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
        </Select>

        {/* Mode */}
        <Select value={filters.mode} onChange={set("mode")}>
          <option value="all">All Modes</option>
          <option value="Online">Online</option>
          <option value="Onsite">Onsite</option>
          <option value="Both">Both</option>
        </Select>

        {/* City */}
        <Select value={filters.city} onChange={set("city")}>
          <option value="all">All Cities</option>
          {cities.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Select>

        {/* Topic */}
        <Select value={filters.topic} onChange={set("topic")}>
          <option value="all">All Topics</option>
          {topics.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Select>

        {/* Reset */}
        {isDirty(filters) && (
          <button
            onClick={onReset}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm
                       text-brand-muted hover:text-white hover:bg-brand-border
                       border border-brand-border transition-colors"
          >
            <X size={13} /> Reset
          </button>
        )}
      </div>
    </div>
  );
};
